// Family Pattern Detector - Analyzes yogas, dashas, composite patterns, and transit sensitivity
// Uses existing data without breaking anything

import { DateTime } from 'luxon';

export interface FamilyMember {
  id: string;
  full_name: string;
  relationship: string;
  chart_data?: any;
  house_scores?: Record<number, number>;
}

export interface YogaPattern {
  type: 'common' | 'complementary' | 'conflicting';
  yogaName: string;
  members: string[]; // member names
  description: string;
  yogaType?: string; // raj, arishta, dhana, etc.
  strength?: string; // weak, moderate, strong, exceptional
}

export interface DashaOverlap {
  type: 'favorable' | 'difficult' | 'mixed';
  members: string[];
  period: { from: string; to: string };
  description: string;
}

export interface CompositePattern {
  type: 'element' | 'planet' | 'quality';
  name: string;
  strength: 'dominant' | 'balanced' | 'missing';
  members: string[];
  description: string;
}

export interface TransitSensitivity {
  planet: string;
  transitingHouse: number;
  affectedMembers: Array<{ name: string; impact: 'positive' | 'negative' | 'neutral'; reason: string }>;
  timeframe: string;
}

export interface FamilyPatterns {
  yogas: YogaPattern[];
  dashaOverlaps: DashaOverlap[];
  compositePatterns: CompositePattern[];
  transitSensitivities: TransitSensitivity[];
  summary: {
    familyStrengths: string[];
    familyChallenges: string[];
    bestTimingWindows: string[];
    supportNetwork: Array<{ supporter: string; supported: string; reason: string }>;
  };
}

/**
 * Detect yoga patterns across family members
 */
export function detectYogaPatterns(familyYogas: Array<{ member: string; yogas: Array<{ yoga_name: string; type: string; strength: string }> }>): YogaPattern[] {
  const patterns: YogaPattern[] = [];
  const yogaMap = new Map<string, string[]>();

  // Group members by yoga
  familyYogas.forEach(({ member, yogas }) => {
    yogas.forEach(({ yoga_name }) => {
      if (!yogaMap.has(yoga_name)) {
        yogaMap.set(yoga_name, []);
      }
      yogaMap.get(yoga_name)!.push(member);
    });
  });

  // Identify common yogas (2+ members)
  yogaMap.forEach((members, yogaName) => {
    if (members.length >= 2) {
      patterns.push({
        type: 'common',
        yogaName,
        members,
        description: `${members.join(', ')} share ${yogaName} - collective ${yogaName.includes('Raj') ? 'prosperity' : 'influence'}`
      });
    }
  });

  // Identify complementary yogas (Dhana + Lakshmi, etc.)
  const hasYoga = (yoga: string) => Array.from(yogaMap.keys()).some(y => y.includes(yoga));
  if (hasYoga('Dhana') && hasYoga('Lakshmi')) {
    patterns.push({
      type: 'complementary',
      yogaName: 'Wealth Synergy',
      members: [...(yogaMap.get('Dhana Yoga') || []), ...(yogaMap.get('Lakshmi Yoga') || [])],
      description: 'Complementary wealth yogas create family prosperity synergy'
    });
  }

  return patterns;
}

/**
 * Detect dasha period overlaps using cached vimshottariTimeline from chart_data
 */
export function detectDashaOverlaps(
  members: FamilyMember[]
): DashaOverlap[] {
  const overlaps: DashaOverlap[] = [];
  const now = DateTime.now();

  // Extract current dasha periods from cached chart_data
  const dashaInfo = members
    .map(m => {
      const chartData = m.chart_data;
      if (!chartData?.vimshottariTimeline || chartData.vimshottariTimeline.length === 0) {
        return null;
      }

      const mdDasha = chartData.vimshottariTimeline[0];
      const adDasha = chartData.vimshottariTimeline[1];
      const blendedPct = chartData.blendedPlanetPct || {};

      // Determine if current period is favorable (MD planet strength > 50%)
      const mdStrength = blendedPct[mdDasha.planet] || 0;
      const adStrength = blendedPct[adDasha?.planet] || 0;
      const avgStrength = (mdStrength + adStrength) / 2;

      return {
        name: m.full_name,
        currentMD: mdDasha.planet,
        currentAD: adDasha?.planet || '',
        mdFrom: mdDasha.from,
        mdTo: mdDasha.to,
        favorable: avgStrength > 50,
        strength: avgStrength
      };
    })
    .filter(d => d !== null) as Array<{
      name: string;
      currentMD: string;
      currentAD: string;
      mdFrom: string;
      mdTo: string;
      favorable: boolean;
      strength: number;
    }>;

  if (dashaInfo.length < 2) return overlaps;

  // Find overlapping favorable periods (2+ members with favorable dashas)
  const favorableMembers = dashaInfo.filter(d => d.favorable);
  if (favorableMembers.length >= 2) {
    const earliestStart = favorableMembers.reduce((earliest, m) => 
      m.mdFrom < earliest ? m.mdFrom : earliest, favorableMembers[0].mdFrom);
    const latestEnd = favorableMembers.reduce((latest, m) => 
      m.mdTo > latest ? m.mdTo : latest, favorableMembers[0].mdTo);

    overlaps.push({
      type: 'favorable',
      members: favorableMembers.map(d => `${d.name} (${d.currentMD})`),
      period: { from: earliestStart, to: latestEnd },
      description: `${favorableMembers.length} members in favorable dasha periods - excellent time for family initiatives and important decisions`
    });
  }

  // Find overlapping difficult periods (2+ members with challenging dashas)
  const difficultMembers = dashaInfo.filter(d => !d.favorable);
  if (difficultMembers.length >= 2) {
    const earliestStart = difficultMembers.reduce((earliest, m) => 
      m.mdFrom < earliest ? m.mdFrom : earliest, difficultMembers[0].mdFrom);
    const latestEnd = difficultMembers.reduce((latest, m) => 
      m.mdTo > latest ? m.mdTo : latest, difficultMembers[0].mdTo);

    overlaps.push({
      type: 'difficult',
      members: difficultMembers.map(d => `${d.name} (${d.currentMD})`),
      period: { from: earliestStart, to: latestEnd },
      description: `${difficultMembers.length} members facing challenging dasha periods - focus on mutual support and patience`
    });
  }

  // Find same MD planet overlaps (shared dasha rulers)
  const mdGroups = new Map<string, typeof dashaInfo>();
  dashaInfo.forEach(d => {
    if (!mdGroups.has(d.currentMD)) mdGroups.set(d.currentMD, []);
    mdGroups.get(d.currentMD)!.push(d);
  });

  mdGroups.forEach((group, planet) => {
    if (group.length >= 2) {
      const earliestStart = group.reduce((earliest, m) => 
        m.mdFrom < earliest ? m.mdFrom : earliest, group[0].mdFrom);
      const latestEnd = group.reduce((latest, m) => 
        m.mdTo > latest ? m.mdTo : latest, group[0].mdTo);

      overlaps.push({
        type: 'mixed',
        members: group.map(d => d.name),
        period: { from: earliestStart, to: latestEnd },
        description: `${group.length} members sharing ${planet} Mahadasha - similar life themes and timing synchronicity`
      });
    }
  });

  return overlaps;
}

/**
 * Detect composite patterns (element/planet dominance) using cached chart_data
 */
export function detectCompositePatterns(members: FamilyMember[]): CompositePattern[] {
  const patterns: CompositePattern[] = [];
  
  // Extract planet strengths from cached blendedPlanetPct
  const planetCounts = new Map<string, number>();
  const strongPlanets = new Map<string, string[]>(); // planet -> members with strong planet

  members.forEach(m => {
    const strengths = m.chart_data?.blendedPlanetPct || {};
    Object.entries(strengths).forEach(([planet, strength]) => {
      if (typeof strength === 'number' && strength > 70) {
        planetCounts.set(planet, (planetCounts.get(planet) || 0) + 1);
        if (!strongPlanets.has(planet)) strongPlanets.set(planet, []);
        strongPlanets.get(planet)!.push(m.full_name);
      }
    });
  });

  // Identify dominant planets (2+ members)
  planetCounts.forEach((count, planet) => {
    if (count >= 2) {
      patterns.push({
        type: 'planet',
        name: planet,
        strength: 'dominant',
        members: strongPlanets.get(planet) || [],
        description: `Strong ${planet} across family suggests ${getPlanetTrait(planet)} family identity`
      });
    }
  });

  // Detect element dominance from ascendant signs (cached in chart_data)
  const elementCounts = new Map<string, { members: string[]; signs: string[] }>();
  
  members.forEach(m => {
    const ascSign = m.chart_data?.ascendant?.sign;
    if (!ascSign) return;

    const element = getSignElement(ascSign);
    if (!elementCounts.has(element)) {
      elementCounts.set(element, { members: [], signs: [] });
    }
    elementCounts.get(element)!.members.push(m.full_name);
    elementCounts.get(element)!.signs.push(getSignName(ascSign));
  });

  // Add element patterns (2+ members)
  elementCounts.forEach(({ members: elementMembers, signs }, element) => {
    if (elementMembers.length >= 2) {
      patterns.push({
        type: 'element',
        name: `${element} Element`,
        strength: elementMembers.length >= 3 ? 'dominant' : 'balanced',
        members: elementMembers,
        description: `${elementMembers.length} members with ${element} ascendants (${[...new Set(signs)].join(', ')}) - family resonates with ${getElementTrait(element)} energy`
      });
    }
  });

  return patterns;
}

/**
 * Detect transit sensitivities
 */
export function detectTransitSensitivities(
  members: FamilyMember[],
  currentTransits: Record<string, { house: number; sign: number }>
): TransitSensitivity[] {
  const sensitivities: TransitSensitivity[] = [];

  // Slow-moving transits (Saturn, Jupiter, Rahu, Ketu)
  const slowPlanets = ['Saturn', 'Jupiter', 'Rahu', 'Ketu'];

  slowPlanets.forEach(planet => {
    const transit = currentTransits[planet];
    if (!transit) return;

    const affectedMembers = members.map(m => {
      const houseScore = m.house_scores?.[transit.house] || 0;
      const impact = houseScore > 50 ? 'positive' : houseScore < -20 ? 'negative' : 'neutral';
      return {
        name: m.full_name,
        impact,
        reason: `${planet} transiting house ${transit.house} (${getHouseSignificance(transit.house)})`
      };
    }).filter(a => a.impact !== 'neutral');

    if (affectedMembers.length > 0) {
      sensitivities.push({
        planet,
        transitingHouse: transit.house,
        affectedMembers: affectedMembers.map(am => ({
          ...am,
          impact: am.impact as 'positive' | 'negative' | 'neutral'
        })),
        timeframe: getTransitTimeframe(planet)
      });
    }
  });

  return sensitivities;
}

/**
 * Generate family patterns summary
 */
export function generateFamilyPatterns(
  members: FamilyMember[],
  familyYogas: Array<{ member: string; yogas: Array<{ yoga_name: string; type: string; strength: string }> }>,
  currentTransits?: Record<string, { house: number; sign: number }>
): FamilyPatterns {
  const yogas = detectYogaPatterns(familyYogas);
  const dashaOverlaps = detectDashaOverlaps(members);
  const compositePatterns = detectCompositePatterns(members);
  const transitSensitivities = currentTransits ? detectTransitSensitivities(members, currentTransits) : [];

  // Generate summary
  const familyStrengths: string[] = [];
  const familyChallenges: string[] = [];
  const bestTimingWindows: string[] = [];
  const supportNetwork: Array<{ supporter: string; supported: string; reason: string }> = [];

  // Extract strengths from patterns
  yogas.filter(y => y.type === 'common' && y.yogaName.includes('Raj')).forEach(y => {
    familyStrengths.push(`Collective ${y.yogaName} brings family-wide prosperity`);
  });

  dashaOverlaps.filter(d => d.type === 'favorable').forEach(d => {
    bestTimingWindows.push(`${d.period.from} to ${d.period.to}: ${d.members.join(', ')} in favorable periods`);
  });

  dashaOverlaps.filter(d => d.type === 'difficult').forEach(d => {
    familyChallenges.push(`${d.members.join(', ')} facing challenging periods - need mutual support`);
  });

  // Detect support network from house scores
  members.forEach(supporter => {
    members.forEach(supported => {
      if (supporter.id === supported.id) return;
      const supporterScores = supporter.house_scores || {};
      const supportedScores = supported.house_scores || {};
      
      // Find complementary houses (supporter's strength matches supported's weakness)
      Object.entries(supportedScores).forEach(([house, score]) => {
        const houseNum = parseInt(house);
        const supporterScore = supporterScores[houseNum] || 0;
        if (score < -20 && supporterScore > 50) {
          supportNetwork.push({
            supporter: supporter.full_name,
            supported: supported.full_name,
            reason: `Strong in H${houseNum} (${getHouseSignificance(houseNum)}) while ${supported.full_name} needs support`
          });
        }
      });
    });
  });

  return {
    yogas,
    dashaOverlaps,
    compositePatterns,
    transitSensitivities,
    summary: {
      familyStrengths,
      familyChallenges,
      bestTimingWindows,
      supportNetwork: supportNetwork.slice(0, 5) // Limit to top 5
    }
  };
}

// Helper functions
function getPlanetTrait(planet: string): string {
  const traits: Record<string, string> = {
    Sun: 'leadership-oriented',
    Moon: 'emotionally connected',
    Mars: 'action-driven',
    Mercury: 'communication-focused',
    Jupiter: 'wisdom-seeking',
    Venus: 'harmony-loving',
    Saturn: 'disciplined and structured'
  };
  return traits[planet] || 'unique';
}

function getSignElement(signNum: number): string {
  const elements = {
    1: 'Fire', 2: 'Earth', 3: 'Air', 4: 'Water',
    5: 'Fire', 6: 'Earth', 7: 'Air', 8: 'Water',
    9: 'Fire', 10: 'Earth', 11: 'Air', 12: 'Water'
  };
  return elements[signNum as keyof typeof elements] || 'Unknown';
}

function getSignName(signNum: number): string {
  const names = [
    'Aries', 'Taurus', 'Gemini', 'Cancer',
    'Leo', 'Virgo', 'Libra', 'Scorpio',
    'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
  ];
  return names[signNum - 1] || 'Unknown';
}

function getElementTrait(element: string): string {
  const traits: Record<string, string> = {
    Fire: 'passionate and dynamic',
    Earth: 'practical and grounded',
    Air: 'intellectual and communicative',
    Water: 'emotional and intuitive'
  };
  return traits[element] || 'balanced';
}

function getHouseSignificance(house: number): string {
  const houses: Record<number, string> = {
    1: 'Self & Identity',
    2: 'Wealth & Family',
    3: 'Communication & Siblings',
    4: 'Home & Mother',
    5: 'Children & Creativity',
    6: 'Health & Service',
    7: 'Partnership & Marriage',
    8: 'Transformation',
    9: 'Luck & Higher Learning',
    10: 'Career & Status',
    11: 'Gains & Networks',
    12: 'Spirituality & Loss'
  };
  return houses[house] || '';
}

function getTransitTimeframe(planet: string): string {
  const timeframes: Record<string, string> = {
    Saturn: '~2.5 years',
    Jupiter: '~1 year',
    Rahu: '~1.5 years',
    Ketu: '~1.5 years'
  };
  return timeframes[planet] || '~6 months';
}
